import React, { useEffect, useState, useRef } from 'react';
import { GripVertical, Copy, Trash2, ChevronDown, ChevronUp, Folder, Plus, Image as ImageIcon, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Card } from './SubscriptionAppCard';
import { Button } from './SubscriptionAppButton';
import { Input } from './SubscriptionAppInput';
import { ProductSourceModal } from './ProductSourceModal';
// Types
export interface Frequency {
  id: string;
  name: string;
  interval: string;
  discount: string;
  settingsText: string;
  isCustomSettings?: boolean;
  hasExpandedSettings?: boolean;
  isRowExpanded?: boolean;
  loyaltyTier?: string;
  adHocDiscount?: string;
  pausedDiscount?: string;
}
export interface PackSize {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  displayLabel: string;
  quantity: string;
  min: number;
  max: number;
  collectionName: string;
  productCount: number;
  frequencies: Frequency[];
  isExpanded: boolean;
}
// Add props interface
interface PackSizesPanelProps {
  boxType?: 'custom' | 'curated';
  onNext?: () => void;
  onBack?: () => void;
  packSizes: PackSize[];
  setPackSizes: React.Dispatch<React.SetStateAction<PackSize[]>>;
}
// Mock Data
const INITIAL_PACK_SIZES: PackSize[] = [];
const INTERVAL_OPTIONS: Record<string, number[]> = {
  Month: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  Week: [1, 2, 3, 4],
  Day: [7, 14, 21, 28, 30, 60, 90]
};
const FREQ_PRESET_TIERS = ['None', 'Bronze', 'Silver', 'Gold', 'Platinum'];
function getIntervalLabel(unit: string, n: number): string {
  const lower = unit.toLowerCase();
  const plural = n === 1 ? lower : `${lower}s`;
  return `Every ${n} ${plural}`;
}
export function PackSizesPanel({
  boxType = 'curated',
  onNext,
  onBack,
  packSizes,
  setPackSizes
}: PackSizesPanelProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackId, setEditingPackId] = useState<string | null>(null);
  const [pendingCustomFreqKey, setPendingCustomFreqKey] = useState<string | null>(null);
  const [customTierValue, setCustomTierValue] = useState('');
  const [freqCustomTiers, setFreqCustomTiers] = useState<string[]>([]);
  const customTierInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (pendingCustomFreqKey && customTierInputRef.current) {
      customTierInputRef.current.focus();
    }
  }, [pendingCustomFreqKey]);
  const handleConfirmFreqCustomTier = (packId: string, freqId: string) => {
    const trimmed = customTierValue.trim();
    if (!trimmed) return;
    if (!freqCustomTiers.includes(trimmed)) {
      setFreqCustomTiers(prev => [...prev, trimmed]);
    }
    updateFrequencyField(packId, freqId, {
      loyaltyTier: trimmed
    });
    setPendingCustomFreqKey(null);
    setCustomTierValue('');
  };
  const addPackSize = () => {
    const newNumber = packSizes.length + 1;
    const newId = `new-${Date.now()}`;
    const newPack: PackSize = {
      id: newId,
      number: newNumber,
      title: 'New Pack Size',
      subtitle: '0 products · 0 frequencies',
      displayLabel: '',
      quantity: '',
      min: 0,
      max: 0,
      collectionName: '',
      productCount: 0,
      frequencies: [],
      isExpanded: true
    };
    setPackSizes(prev => [...prev, newPack]);
  };
  const deletePackSize = (id: string) => {
    setPackSizes(prev => prev.filter(pack => pack.id !== id).map((pack, index) => ({
      ...pack,
      number: index + 1
    })));
  };
  const duplicatePackSize = (id: string) => {
    setPackSizes(prev => {
      const original = prev.find(pack => pack.id === id);
      if (!original) return prev;
      const newId = `dup-${Date.now()}`;
      const duplicate: PackSize = {
        ...original,
        id: newId,
        displayLabel: original.displayLabel ? `${original.displayLabel} - Copy` : 'Copy',
        title: original.title ? `${original.title} - Copy` : 'Copy',
        isExpanded: true,
        frequencies: original.frequencies.map(freq => ({
          ...freq,
          id: `${freq.id}-dup-${Date.now()}`
        }))
      };
      const index = prev.findIndex(pack => pack.id === id);
      const newList = [...prev];
      newList.splice(index + 1, 0, duplicate);
      return newList.map((pack, i) => ({
        ...pack,
        number: i + 1
      }));
    });
  };
  const handleOpenProductSource = (packId: string) => {
    const pack = packSizes.find(p => p.id === packId);
    if (boxType === 'curated' && (!pack?.quantity || parseInt(pack.quantity) <= 0)) return;
    setEditingPackId(packId);
    setIsModalOpen(true);
  };
  const handleConfirmProductSource = (selectedIds: string[], quantities?: Record<string, number>) => {
    if (editingPackId) {
      const productCount = selectedIds.length;
      let subtitle = '';
      if (boxType === 'curated' && quantities) {
        const totalBottles = Object.values(quantities).reduce((a, b) => a + b, 0);
        subtitle = `Curated · ${productCount} products · ${totalBottles} bottles`;
      } else {
        subtitle = `Manual Selection · ${productCount} products · ${packSizes.find(p => p.id === editingPackId)?.frequencies.length || 0} frequencies`;
      }
      setPackSizes(prev => prev.map(pack => pack.id === editingPackId ? {
        ...pack,
        collectionName: boxType === 'curated' ? 'Curated Selection' : 'Manual Selection',
        productCount,
        subtitle
      } : pack));
    }
    setIsModalOpen(false);
    setEditingPackId(null);
  };
  const togglePackSize = (id: string) => {
    setPackSizes(prev => prev.map(pack => pack.id === id ? {
      ...pack,
      isExpanded: !pack.isExpanded
    } : pack));
  };
  const toggleFrequencySettings = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.map(freq => freq.id === freqId ? {
          ...freq,
          hasExpandedSettings: !freq.hasExpandedSettings
        } : freq)
      };
    }));
  };
  const addFrequency = (packId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      const newFreq: Frequency = {
        id: `freq-${Date.now()}`,
        name: '',
        interval: '',
        discount: '',
        settingsText: 'Inherits defaults',
        isCustomSettings: false,
        hasExpandedSettings: true,
        isRowExpanded: true
      };
      return {
        ...pack,
        frequencies: [...pack.frequencies, newFreq]
      };
    }));
  };
  const deleteFrequency = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.filter(freq => freq.id !== freqId)
      };
    }));
  };
  const updateFrequencyField = (packId: string, freqId: string, updates: Partial<Frequency>) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.map(freq => freq.id === freqId ? {
          ...freq,
          ...updates
        } : freq)
      };
    }));
  };
  const duplicateFrequency = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      const original = pack.frequencies.find(f => f.id === freqId);
      if (!original) return pack;
      const duplicate: Frequency = {
        ...original,
        id: `freq-dup-${Date.now()}`
      };
      const index = pack.frequencies.findIndex(f => f.id === freqId);
      const newFreqs = [...pack.frequencies];
      newFreqs.splice(index + 1, 0, duplicate);
      return {
        ...pack,
        frequencies: newFreqs
      };
    }));
  };
  const toggleFrequencyRow = (packId: string, freqId: string) => {
    setPackSizes(prev => prev.map(pack => {
      if (pack.id !== packId) return pack;
      return {
        ...pack,
        frequencies: pack.frequencies.map(freq => freq.id === freqId ? {
          ...freq,
          isRowExpanded: !freq.isRowExpanded
        } : freq)
      };
    }));
  };
  const updatePackField = (packId: string, updates: Partial<PackSize>) => {
    setPackSizes(prev => prev.map(pack => pack.id === packId ? {
      ...pack,
      ...updates
    } : pack));
  };
  const editingPack = packSizes.find(p => p.id === editingPackId);
  const packMin = editingPack ? editingPack.min : 0;
  const packMax = editingPack ? editingPack.max : 0;
  return <div className="flex flex-col gap-4 max-w-5xl mx-auto" data-id="element-2043">
      <button className="w-full py-4 border-2 border-dashed border-[#CBD5E1] rounded-xl flex items-center justify-center gap-2 text-[14px] font-medium text-[#94A3B8] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors duration-200" onClick={addPackSize} data-id="element-2044">
        <Plus className="w-4 h-4" data-id="element-2045" />
        Add Pack Size
      </button>

      <ProductSourceModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingPackId(null);
    }} onConfirm={handleConfirmProductSource} mode={boxType} packMin={packMin} packMax={packMax} data-id="element-2046" />

      {packSizes.map(pack => <Card key={pack.id} padding="none" className="overflow-hidden border border-[#E2E8F0] shadow-sm" data-id="element-2047">
          <div className={`flex items-center justify-between px-4 py-4 cursor-pointer transition-colors ${pack.isExpanded ? 'bg-white border-b border-[#E2E8F0]' : 'bg-white hover:bg-[#F8FAFC]'}`} onClick={() => togglePackSize(pack.id)} data-id="element-2048">
            <div className="flex items-center gap-4" data-id="element-2049">
              <div className="text-[#94A3B8] cursor-grab active:cursor-grabbing p-1 hover:bg-[#F1F5F9] rounded" data-id="element-2050">
                <GripVertical className="w-5 h-5" data-id="element-2051" />
              </div>
              <div className="w-7 h-7 rounded-full bg-[#0EA5E9] text-white text-[13px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-2052">
                {pack.number}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3" data-id="element-2053">
                <span className="text-[14px] font-bold text-[#0F172A]" data-id="element-2054">
                  {pack.displayLabel || pack.title}
                </span>
                <span className="hidden sm:inline text-[#CBD5E1]" data-id="element-2055">•</span>
                <span className="text-[13px] text-[#64748B]" data-id="element-2056">
                  {pack.productCount} products · {pack.frequencies.length}{' '}
                  frequencies
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-2057">
              <button className="p-2 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] rounded-md transition-colors" onClick={e => {
            e.stopPropagation();
            duplicatePackSize(pack.id);
          }} title="Duplicate Pack & Frequencies" data-id="element-2058">
                <Copy className="w-4 h-4" data-id="element-2059" />
              </button>
              <button className="p-2 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded-md transition-colors" onClick={e => {
            e.stopPropagation();
            deletePackSize(pack.id);
          }} title="Delete" data-id="element-2060">
                <Trash2 className="w-4 h-4" data-id="element-2061" />
              </button>
              <div className="w-px h-6 bg-[#E2E8F0] mx-1" data-id="element-2062" />
              <button className="p-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-2063">
                {pack.isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-2064" /> : <ChevronDown className="w-5 h-5" data-id="element-2065" />}
              </button>
            </div>
          </div>

          {pack.isExpanded && <div className="p-6 space-y-8 bg-white" data-id="element-2066">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2067">
                <div data-id="element-2068">
                  <Input label="Display Name" required value={pack.displayLabel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePackField(pack.id, {
              displayLabel: e.target.value
            })} placeholder="e.g. Six Pack" data-id="element-2069" />
                  <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2070">
                    Shown to customers in the signup flow pack selector
                  </p>
                </div>
                {boxType === 'custom' ? <div data-id="element-2071">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2072">
                      Quantity <span className="text-[#EF4444]" data-id="element-2073">*</span>
                    </label>
                    <input type="number" value={pack.quantity} onChange={e => updatePackField(pack.id, {
              quantity: e.target.value
            })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2074" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2075">
                      Number of bottles the customer picks for this pack
                    </p>
                  </div> : <div data-id="element-2076">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2077">
                      Quantity <span className="text-[#EF4444]" data-id="element-2078">*</span>
                    </label>
                    <input type="number" value={pack.quantity} onChange={e => {
              const val = e.target.value;
              const numVal = parseInt(val) || 0;
              updatePackField(pack.id, {
                quantity: val,
                min: numVal,
                max: numVal
              });
            }} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2079" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2080">
                      Number of bottles in this curated pack. Product selections
                      must match this quantity.
                    </p>
                  </div>}
              </div>

              {boxType === 'custom' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2081">
                  <div data-id="element-2082">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2083">
                      Minimum Bottles
                    </label>
                    <input type="number" value={pack.min || ''} onChange={e => updatePackField(pack.id, {
              min: parseInt(e.target.value) || 0
            })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2084" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2085">
                      For custom boxes — minimum the customer must select
                    </p>
                  </div>
                  <div data-id="element-2086">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2087">
                      Maximum Bottles
                    </label>
                    <input type="number" value={pack.max || ''} onChange={e => updatePackField(pack.id, {
              max: parseInt(e.target.value) || 0
            })} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2088" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2089">
                      Set higher than quantity to allow flexibility (e.g. 6–8)
                    </p>
                  </div>
                </div>}

              <div data-id="element-2090">
                <h3 className="text-[14px] font-bold text-[#0F172A] mb-3" data-id="element-2091">
                  Product Source
                </h3>
                {boxType === 'curated' && (!pack.quantity || parseInt(pack.quantity) <= 0) ? <div className="border border-dashed border-[#CBD5E1] rounded-lg p-4 flex items-center justify-between bg-[#F8FAFC]" data-id="element-2092">
                    <div className="flex items-center gap-3" data-id="element-2093">
                      <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center text-[#94A3B8]" data-id="element-2094">
                        <Folder className="w-5 h-5" data-id="element-2095" />
                      </div>
                      <div data-id="element-2096">
                        <div className="text-[14px] font-medium text-[#94A3B8]" data-id="element-2097">
                          Set a quantity first
                        </div>
                        <div className="text-[13px] text-[#CBD5E1]" data-id="element-2098">
                          Enter the number of bottles above to enable product
                          selection
                        </div>
                      </div>
                    </div>
                    <span className="text-[13px] font-medium text-[#CBD5E1] cursor-not-allowed" data-id="element-2099">
                      Change
                    </span>
                  </div> : <div className="border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between bg-white" data-id="element-2100">
                    <div className="flex items-center gap-3" data-id="element-2101">
                      <div className="w-10 h-10 rounded-lg bg-[#E0F2FE] flex items-center justify-center text-[#0EA5E9]" data-id="element-2102">
                        <Folder className="w-5 h-5" data-id="element-2103" />
                      </div>
                      <div data-id="element-2104">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-2105">
                          {pack.collectionName || (boxType === 'curated' ? 'No products selected' : 'No collection selected')}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-2106">
                          {pack.productCount} products
                        </div>
                      </div>
                    </div>
                    <button className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline" onClick={() => handleOpenProductSource(pack.id)} data-id="element-2107">
                      Change
                    </button>
                  </div>}
                <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2108">
                  Search for products synced from your Shopify account
                </p>
                <div className="mt-3 flex items-start gap-3 bg-[#E0F2FE] border border-[#BAE6FD] rounded-lg px-4 py-3" data-id="element-2109">
                  <svg className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-2110">
                    <circle cx="12" cy="12" r="10" data-id="element-2111" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" data-id="element-2112" />
                  </svg>
                  <p className="text-[13px] text-[#0369A1] leading-relaxed" data-id="element-2113">
                    Each pack size has its own collection. This lets you offer
                    exclusive products in larger packs — for example, museum
                    releases only available in the 12-pack.
                  </p>
                </div>
              </div>

              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-2114">
                <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-2115">
                  <span className="text-[11px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-2116">
                    Storefront Content
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2117">
                  <div data-id="element-2118">
                    <label className="block text-[13px] font-semibold text-[#0F172A] mb-2" data-id="element-2119">
                      Pack Description
                    </label>
                    <textarea placeholder="e.g. Choose your own selection of six wines from our estate cellar." rows={4} className="w-full px-3 py-2.5 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-2120" />
                    <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2121">
                      Shown on the pack card in Step 1 of the signup flow
                    </p>
                  </div>
                  <div data-id="element-2122">
                    <label className="block text-[13px] font-semibold text-[#0F172A] mb-2" data-id="element-2123">
                      Pack Image
                    </label>
                    <div className="flex items-start gap-4 border border-dashed border-[#CBD5E1] rounded-[6px] p-4 bg-[#F8FAFC]" data-id="element-2124">
                      <div className="w-14 h-14 rounded-[6px] border border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0 bg-white" data-id="element-2125">
                        <ImageIcon className="w-6 h-6" data-id="element-2126" />
                      </div>
                      <div data-id="element-2127">
                        <p className="text-[13px] text-[#475569]" data-id="element-2128">
                          <span className="font-semibold text-[#0F172A]" data-id="element-2129">
                            Upload image
                          </span>{' '}
                          or drag and drop
                        </p>
                        <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-2130">
                          Displayed on the pack selection card. Recommended 600
                          x 400px.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-2131">
                <div className="mb-4" data-id="element-2132">
                  <h3 className="text-[16px] font-bold text-[#0F172A]" data-id="element-2133">
                    Frequencies, Pricing & Membership Tiers
                  </h3>
                </div>
                <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-2134">
                  <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-2135">
                    <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-2136">
                      Frequency
                    </div>
                    <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-2137">
                      Interval
                    </div>
                    <div className="col-span-2 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-2138">
                      Discount
                    </div>
                    <div className="col-span-3 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-2139">
                      Discount Type
                    </div>
                    <div className="col-span-1 text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px] text-right" data-id="element-2140">
                      Actions
                    </div>
                  </div>
                  <div className="divide-y divide-[#E2E8F0]" data-id="element-2141">
                    {pack.frequencies.map(freq => <div key={freq.id} className={`${freq.isRowExpanded === false ? 'bg-white border-l-[3px] border-l-[#10B981]' : 'bg-[#F0F9FF] border-l-[3px] border-l-[#0EA5E9]'}`} data-id="element-2142">
                        {freq.isRowExpanded === false ? <div className="flex items-center justify-between px-4 py-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => toggleFrequencyRow(pack.id, freq.id)} data-id="element-2143">
                            <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-2144">
                              <div className="w-2 h-2 rounded-full bg-[#10B981] flex-shrink-0" data-id="element-2145" />
                              <span className="text-[14px] font-semibold text-[#0F172A] flex-shrink-0" data-id="element-2146">
                                {freq.interval || freq.name || 'New Frequency'}
                              </span>
                              {freq.discount && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold bg-[#D1FAE5] text-[#059669] flex-shrink-0" data-id="element-2147">
                                  {freq.discount}
                                </span>}
                              <span className="text-[13px] text-[#94A3B8] font-mono truncate hidden sm:block" data-id="element-2148">
                                sp_
                                {freq.name ? freq.name.toLowerCase() : 'plan'}_
                                {freq.interval ? freq.interval.replace(/\s+/g, '_').toLowerCase() : 'interval'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2149">
                              <button className="p-1.5 text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9] rounded transition-colors" onClick={e => {
                      e.stopPropagation();
                      duplicateFrequency(pack.id, freq.id);
                    }} data-id="element-2150">
                                <Copy className="w-4 h-4" data-id="element-2151" />
                              </button>
                              <button className="p-1.5 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" onClick={e => {
                      e.stopPropagation();
                      deleteFrequency(pack.id, freq.id);
                    }} data-id="element-2152">
                                <Trash2 className="w-4 h-4" data-id="element-2153" />
                              </button>
                              <ChevronDown className="w-4 h-4 text-[#94A3B8] ml-1" data-id="element-2154" />
                            </div>
                          </div> : <>
                            <div className="flex items-center justify-between px-4 py-2 bg-[#E0F2FE] border-b border-[#BAE6FD]" data-id="element-2155">
                              <div className="flex items-center gap-2" data-id="element-2156">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-[#0EA5E9] text-white uppercase tracking-wide" data-id="element-2157">
                                  Editing
                                </span>
                                {freq.name || freq.interval ? <span className="text-[13px] font-semibold text-[#0369A1]" data-id="element-2158">
                                    {freq.interval || freq.name}
                                  </span> : <span className="text-[13px] text-[#0EA5E9]" data-id="element-2159">
                                    New frequency — select options below
                                  </span>}
                              </div>
                              <button className="flex items-center gap-1 text-[12px] font-medium text-[#0369A1] hover:text-[#0F172A] hover:bg-[#BAE6FD] px-2 py-1 rounded transition-colors" onClick={() => toggleFrequencyRow(pack.id, freq.id)} data-id="element-2160">
                                <ChevronUp className="w-3.5 h-3.5" data-id="element-2161" />
                                Collapse
                              </button>
                            </div>
                            <div className="grid grid-cols-12 gap-4 px-4 py-4 items-center bg-white" data-id="element-2162">
                              <div className="col-span-3" data-id="element-2163">
                                <div className="relative" data-id="element-2164">
                                  <select value={freq.name} onChange={e => updateFrequencyField(pack.id, freq.id, {
                          name: e.target.value,
                          interval: ''
                        })} className="w-full h-[36px] px-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-2165">
                                    <option value="" disabled data-id="element-2166">
                                      Select...
                                    </option>
                                    <option value="Day" data-id="element-2167">Day</option>
                                    <option value="Week" data-id="element-2168">Week</option>
                                    <option value="Month" data-id="element-2169">Month</option>
                                  </select>
                                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-2170" />
                                </div>
                              </div>
                              <div className="col-span-3" data-id="element-2171">
                                <div className="relative" data-id="element-2172">
                                  <select value={freq.interval} disabled={!freq.name} onChange={e => updateFrequencyField(pack.id, freq.id, {
                          interval: e.target.value
                        })} className="w-full h-[36px] px-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#475569] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer disabled:bg-[#F1F5F9] disabled:text-[#94A3B8] disabled:cursor-not-allowed" data-id="element-2173">
                                    <option value="" disabled data-id="element-2174">
                                      {freq.name ? 'Select interval...' : 'Select frequency first'}
                                    </option>
                                    {freq.name && (INTERVAL_OPTIONS[freq.name] ?? []).map(n => {
                            const label = getIntervalLabel(freq.name, n);
                            return <option key={n} value={label} data-id="element-2175">
                                              {label}
                                            </option>;
                          })}
                                  </select>
                                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-2176" />
                                </div>
                              </div>
                              <div className="col-span-2" data-id="element-2177">
                                {freq.discount ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#D1FAE5] text-[#059669]" data-id="element-2178">
                                    {freq.discount}
                                  </span> : <input type="text" placeholder="e.g. 10" defaultValue={freq.discount} className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2179" />}
                              </div>
                              <div className="col-span-3 flex items-center" data-id="element-2180">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#F1F5F9] border border-[#E2E8F0] text-[13px] font-medium text-[#475569]" data-id="element-2181">
                                  <span className="text-[15px] font-semibold text-[#0F172A]" data-id="element-2182">
                                    %
                                  </span>
                                  Percentage
                                </span>
                              </div>
                              <div className="col-span-1 flex items-center justify-end" data-id="element-2183">
                                <button className="p-1.5 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" onClick={e => {
                        e.stopPropagation();
                        deleteFrequency(pack.id, freq.id);
                      }} data-id="element-2184">
                                  <Trash2 className="w-4 h-4" data-id="element-2185" />
                                </button>
                              </div>
                            </div>
                            <div className="border-t border-[#E2E8F0] bg-[#FAFAFA]" data-id="element-2186">
                              <div className="px-4 pt-4" data-id="element-2187">
                                <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2188">
                                  <div data-id="element-2189">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2190">
                                      Billing Anchor
                                    </label>
                                    <div className="relative" data-id="element-2191">
                                      <select className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-2192">
                                        <option data-id="element-2193">Signup date</option>
                                        <option data-id="element-2194">1st of month</option>
                                        <option data-id="element-2195">15th of month</option>
                                      </select>
                                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-2196" />
                                    </div>
                                  </div>
                                  <div data-id="element-2197">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2198">
                                      Min Commitment
                                    </label>
                                    <input type="number" defaultValue="3" className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2199" />
                                    <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-2200">
                                      Cycles before cancel allowed
                                    </p>
                                  </div>
                                  <div data-id="element-2201">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2202">
                                      Edit Cutoff
                                    </label>
                                    <input type="number" defaultValue="5" className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2203" />
                                    <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-2204">
                                      Days before billing
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2205">
                                  <div data-id="element-2206">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2207">
                                      Loyalty Tier
                                    </label>
                                    <div className="relative" data-id="element-2208">
                                      <select value={FREQ_PRESET_TIERS.includes(freq.loyaltyTier || '') ? freq.loyaltyTier || 'None' : freqCustomTiers.includes(freq.loyaltyTier || '') ? freq.loyaltyTier : pendingCustomFreqKey === `${pack.id}-${freq.id}` ? '__custom__' : freq.loyaltyTier || 'None'} onChange={e => {
                              if (e.target.value === '__custom__') {
                                setPendingCustomFreqKey(`${pack.id}-${freq.id}`);
                                setCustomTierValue('');
                              } else {
                                setPendingCustomFreqKey(null);
                                updateFrequencyField(pack.id, freq.id, {
                                  loyaltyTier: e.target.value
                                });
                              }
                            }} className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-2209">
                                        {FREQ_PRESET_TIERS.map(tier => <option key={tier} value={tier} data-id="element-2210">
                                            {tier}
                                          </option>)}
                                        {freqCustomTiers.length > 0 && <option disabled data-id="element-2211">──────────</option>}
                                        {freqCustomTiers.map(tier => <option key={tier} value={tier} data-id="element-2212">
                                            {tier}
                                          </option>)}
                                        <option value="__custom__" data-id="element-2213">
                                          Custom...
                                        </option>
                                      </select>
                                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-2214" />
                                    </div>
                                    {pendingCustomFreqKey === `${pack.id}-${freq.id}` && <div className="mt-2 flex items-center gap-2" data-id="element-2215">
                                        <input ref={customTierInputRef} type="text" value={customTierValue} onChange={e => setCustomTierValue(e.target.value)} onKeyDown={e => {
                              if (e.key === 'Enter') handleConfirmFreqCustomTier(pack.id, freq.id);
                              if (e.key === 'Escape') {
                                setPendingCustomFreqKey(null);
                                setCustomTierValue('');
                              }
                            }} placeholder="Enter tier name..." className="flex-1 h-[32px] px-3 bg-white border border-[#0EA5E9] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2216" />
                                        <button onMouseDown={e => {
                              e.preventDefault();
                              handleConfirmFreqCustomTier(pack.id, freq.id);
                            }} disabled={!customTierValue.trim()} className="w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center rounded-[6px] bg-[#0EA5E9] text-white hover:bg-[#0284C7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors z-10 relative" data-id="element-2217">
                                          <Check className="w-3.5 h-3.5" data-id="element-2218" />
                                        </button>
                                      </div>}
                                    <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-2219">
                                      Tier assigned to subscribers on this plan
                                    </p>
                                  </div>
                                  <div data-id="element-2220">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2221">
                                      Ad-Hoc Discount
                                    </label>
                                    <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-2222">
                                      <input type="number" value={freq.adHocDiscount ?? '0'} onChange={e => updateFrequencyField(pack.id, freq.id, {
                              adHocDiscount: e.target.value
                            })} className="flex-1 h-[36px] px-3 bg-white text-[13px] text-[#0F172A] focus:outline-none min-w-0" data-id="element-2223" />
                                      <div className="bg-[#F8FAFC] px-3 flex items-center text-[13px] text-[#64748B] border-l border-[#CBD5E1] flex-shrink-0" data-id="element-2224">
                                        %
                                      </div>
                                    </div>
                                    <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-2225">
                                      Member discount on non-subscription
                                      purchases
                                    </p>
                                  </div>
                                  <div data-id="element-2226">
                                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-2227">
                                      Paused Discount
                                    </label>
                                    <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-2228">
                                      <input type="number" value={freq.pausedDiscount ?? '0'} onChange={e => updateFrequencyField(pack.id, freq.id, {
                              pausedDiscount: e.target.value
                            })} className="flex-1 h-[36px] px-3 bg-white text-[13px] text-[#0F172A] focus:outline-none min-w-0" data-id="element-2229" />
                                      <div className="bg-[#F8FAFC] px-3 flex items-center text-[13px] text-[#64748B] border-l border-[#CBD5E1] flex-shrink-0" data-id="element-2230">
                                        %
                                      </div>
                                    </div>
                                    <p className="text-[11px] text-[#94A3B8] mt-1" data-id="element-2231">
                                      Discount applied when this subscription is
                                      paused
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-4 border-t-2 border-dashed border-[#E2E8F0] pt-4" data-id="element-2232">
                                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-5" data-id="element-2233">
                                    <div className="flex justify-end" data-id="element-2234">
                                      <span className="text-[11px] font-semibold text-[#0EA5E9] uppercase tracking-[0.5px]" data-id="element-2235">
                                        Storefront Card Content For This
                                        Frequency
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2236">
                                      <div data-id="element-2237">
                                        <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-2238">
                                          Card Tagline
                                        </label>
                                        <input type="text" placeholder="e.g. Skip, cancel or change at any time" className="w-full h-[40px] px-3 bg-white border border-[#E2E8F0] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2239" />
                                        <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2240">
                                          Text shown below the frequency name on
                                          the card
                                        </p>
                                      </div>
                                      <div data-id="element-2241">
                                        <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-2242">
                                          CTA Text
                                        </label>
                                        <input type="text" placeholder="e.g. First box ships today!" className="w-full h-[40px] px-3 bg-white border border-[#E2E8F0] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-2243" />
                                        <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-2244">
                                          Button or label text on the frequency
                                          card
                                        </p>
                                      </div>
                                    </div>
                                    <div data-id="element-2245">
                                      <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-2246">
                                        Card Image
                                      </label>
                                      <div className="flex items-center gap-4" data-id="element-2247">
                                        <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#CBD5E1] flex items-center justify-center text-[#94A3B8] flex-shrink-0" data-id="element-2248">
                                          <ImageIcon className="w-6 h-6" data-id="element-2249" />
                                        </div>
                                        <div data-id="element-2250">
                                          <p className="text-[13px] text-[#475569]" data-id="element-2251">
                                            <span className="font-bold text-[#0F172A]" data-id="element-2252">
                                              Upload image
                                            </span>{' '}
                                            or drag and drop
                                          </p>
                                          <p className="text-[12px] text-[#94A3B8] mt-0.5" data-id="element-2253">
                                            PNG, JPG up to 2MB. Displayed at top
                                            of frequency card.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div data-id="element-2254">
                                      <label className="block text-[12px] font-bold text-[#334155] mb-1.5" data-id="element-2255">
                                        Card CSS
                                      </label>
                                      <textarea defaultValue={`/* Target this frequency card */\n.freq-card--${freq.name ? freq.name.toLowerCase().replace(/\s+/g, '-') : 'monthly'} { }`} className="w-full h-[80px] px-4 py-3 bg-[#0F172A] border border-[#334155] rounded-[6px] text-[13px] text-[#E2E8F0] font-mono focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" data-id="element-2256" />
                                      <p className="text-[12px] text-[#94A3B8] mt-1.5 text-right" data-id="element-2257">
                                        CSS scoped to this frequency card only.
                                        Class:{' '}
                                        <code className="font-mono text-[#64748B]" data-id="element-2258">
                                          .freq-card--{'{handle}'}
                                        </code>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center justify-between px-4 py-3 mt-2 border-t border-[#E2E8F0]" data-id="element-2259">
                                <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2] px-2 py-1.5 rounded-md transition-colors" onClick={() => deleteFrequency(pack.id, freq.id)} data-id="element-2260">
                                  <Trash2 className="w-3.5 h-3.5" data-id="element-2261" />
                                  Delete Frequency
                                </button>
                                <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] px-2 py-1.5 rounded-md transition-colors" onClick={() => duplicateFrequency(pack.id, freq.id)} data-id="element-2262">
                                  <Copy className="w-3.5 h-3.5" data-id="element-2263" />
                                  Duplicate
                                </button>
                              </div>
                            </div>
                          </>}
                      </div>)}
                  </div>
                  <div className="px-4 py-3 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center" data-id="element-2264">
                    <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#475569] border border-dashed border-[#CBD5E1] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] px-3 py-1.5 rounded-md transition-colors" onClick={() => addFrequency(pack.id)} data-id="element-2265">
                      <Plus className="w-3.5 h-3.5" data-id="element-2266" />
                      Add Frequency
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between px-1 py-2 border-t border-[#E2E8F0]" data-id="element-2267">
                  <button className="flex items-center gap-2 text-[13px] font-medium text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2] px-3 py-2 rounded-md transition-colors" onClick={() => deletePackSize(pack.id)} data-id="element-2268">
                    <Trash2 className="w-4 h-4" data-id="element-2269" />
                    Delete Pack Size
                  </button>
                  <button className="flex items-center gap-2 text-[13px] font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] px-3 py-2 rounded-md transition-colors" onClick={() => duplicatePackSize(pack.id)} data-id="element-2270">
                    <Copy className="w-4 h-4" data-id="element-2271" />
                    Duplicate Pack & Frequencies
                  </button>
                </div>
              </div>
            </div>}
        </Card>)}

      <div className="flex items-center justify-between pt-2" data-id="element-2272">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-2273">
          <ArrowLeft className="w-4 h-4" data-id="element-2274" />
          Basics
        </button>
        <Button variant="primary" onClick={onNext} disabled={packSizes.length === 0} iconRight={<ArrowRight className="w-4 h-4" data-id="element-2276" />} data-id="element-2275">
          Next: Membership
        </Button>
      </div>
    </div>;
}